import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

interface AdminAuthContextValue {
  isAdminAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  handleUnauthorized: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

// Persisted in localStorage (not localStorage) so the admin "logged in" hint
// is shared across tabs — opening an admin link (e.g. "Preview as customer") in
// a new tab honours the existing session instead of bouncing to login. The real
// session is the HttpOnly admin_session_id cookie (also shared across tabs); a
// stale flag self-heals via handleUnauthorized on the next 401/403.
const SESSION_KEY = 'loupe_admin_authenticated';

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(SESSION_KEY);
    setIsAdminAuthenticated(stored === 'true');
    setLoading(false);
  }, []);

  const login = useCallback(async (password: string, totpCode?: string) => {
    setError(null);
    try {
      const res = await fetch('/api/v1/admin/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // `totp_code` is omitted by JSON.stringify when undefined, so this stays
        // compatible with the password-only flow before MFA is enrolled.
        body: JSON.stringify({ password, totp_code: totpCode || undefined }),
      });

      if (res.ok) {
        localStorage.setItem(SESSION_KEY, 'true');
        setIsAdminAuthenticated(true);
      } else if (res.status === 401) {
        setError('Invalid credentials');
      } else if (res.status === 429) {
        setError('Too many failed attempts. Please wait and try again.');
      } else {
        setError('Login temporarily unavailable');
      }
    } catch {
      setError('Login temporarily unavailable');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/v1/admin/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      localStorage.removeItem(SESSION_KEY);
      setIsAdminAuthenticated(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setIsAdminAuthenticated(false);
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{ isAdminAuthenticated, loading, error, login, logout, clearError, handleUnauthorized }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextValue {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
